import type { EntryAnalysis, EntryAnalysisFinding } from "@seagames/contracts";
import { useUiStrings } from "@seagames/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../lib/api/client";
import { entryAnalysisKeys, entryAnalysisOptions, refreshEntryAnalysis } from "../../lib/api/entries";
import { errorMessage } from "../../lib/api/messages";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import "./entries-play.css";

/** While another request is making the analysis, read again this often (and stop after this long). */
const POLL_MS = 4_000;
const POLL_MAX_MS = 150_000;

/**
 * The AI analysis on a NOC's EOI / EBN editor — admins only (the caller
 * renders it for the admin view; the API answers 403 to anyone else).
 *
 * The analysis is structured findings, stored by the API and shown as is on
 * every visit; each sentence comes from ONE fixed template per kind
 * (`analysis.finding.*`), so the same facts always read the same, in the
 * viewer's language. When the NOC's data changed since (`stale`), the panel
 * asks for a refresh ONCE per visit and keeps showing the last analysis,
 * marked "Updating", until the new one arrives. Claude down (503) or the
 * daily cap (429) leaves the last analysis on screen with a note. No sources
 * or "retrieved" lines, ever (user, 2026-10-30).
 */
export function EntryAnalysisPanel({ gamesId, orgId }: { gamesId: string; orgId: string }) {
	const { t } = useTranslation("entries");
	const qc = useQueryClient();
	const scope = { games_id: gamesId, organization_id: orgId };
	const [polling, setPolling] = useState<number | null>(null);
	const q = useQuery({
		...entryAnalysisOptions(scope),
		refetchInterval: (query) => (polling !== null && query.state.data?.stale ? POLL_MS : false),
	});
	const refresh = useMutation({
		mutationFn: () => refreshEntryAnalysis(scope),
		onSuccess: ({ outcome, ...view }) => {
			if (outcome === "IN_PROGRESS") setPolling(Date.now());
			else qc.setQueryData<EntryAnalysis>(entryAnalysisKeys.one(scope), view);
		},
	});

	// Polling for another request's analysis stops after a while; the last analysis stays on screen.
	useEffect(() => {
		if (polling === null) return;
		const id = setTimeout(() => setPolling(null), POLL_MAX_MS);
		return () => clearTimeout(id);
	}, [polling]);

	// Once per visit: the data moved since the stored analysis (or there is none yet).
	const asked = useRef(false);
	const stale = q.data?.availability === "AVAILABLE" && q.data.stale;
	useEffect(() => {
		if (stale && !asked.current) {
			asked.current = true;
			refresh.mutate();
		}
	}, [stale, refresh.mutate]);

	if (q.isPending) return <div className="skeleton h-[6rem] rounded-[0.625rem]" aria-busy />;
	if (q.isError)
		return (
			<section className="ent-hist ent-ai" aria-label={t("analysis.title")}>
				<Head />
				<p className="ent-ai__note" data-tone="bad">
					{errorMessage(q.error)}
				</p>
			</section>
		);
	const view = q.data;
	if (view.availability === "PARA_GAMES") return null;

	const working = stale && (refresh.isPending || polling !== null || (refresh.isIdle && !asked.current));
	const failed = stale && refresh.isError ? refresh.error : null;
	const findings = view.analysis?.findings ?? [];

	return (
		<section className="ent-hist ent-ai" aria-label={t("analysis.title")} aria-busy={working || undefined}>
			<Head
				status={
					view.analysis && working ? (
						<span className="ent-ai__status" role="status">
							{t("analysis.updating")}
						</span>
					) : view.analysis ? (
						<span className="ent-hist__sub" title={formatDateTime(view.analysis.generated_at)}>
							{t("analysis.updated", { when: formatShort(view.analysis.generated_at) })}
						</span>
					) : null
				}
			/>
			{view.availability === "NOT_IN_HISTORY" ? (
				<p className="ent-ai__note">{t("analysis.notInHistory")}</p>
			) : !view.analysis && working ? (
				<div className="grid gap-[0.375rem]" role="status" aria-label={t("analysis.analysing")}>
					<span className="ent-ai__note">{t("analysis.analysing")}</span>
					<div className="skeleton h-[0.875rem] w-[80%] rounded" />
					<div className="skeleton h-[0.875rem] w-[65%] rounded" />
					<div className="skeleton h-[0.875rem] w-[72%] rounded" />
				</div>
			) : view.analysis && findings.length === 0 ? (
				<p className="ent-ai__note">{t("analysis.nothing")}</p>
			) : view.analysis ? (
				<ul className="ent-ai__list">
					{findings.map((f) => (
						<FindingRow key={`${f.kind}|${"sport" in f ? f.sport.code : ""}|${"measure" in f ? f.measure : ""}`} finding={f} noc={view.organization.name} />
					))}
				</ul>
			) : null}
			{failed && (
				<p className="ent-ai__note" data-tone="bad">
					{failureText(failed, !!view.analysis, t)}{" "}
					{!(failed instanceof ApiError && failed.code === "RATE_LIMITED") && (
						<button type="button" className="ent-ai__retry" onClick={() => refresh.mutate()}>
							{t("analysis.retry")}
						</button>
					)}
				</p>
			)}
		</section>
	);
}

function Head({ status }: { status?: React.ReactNode }) {
	const { t } = useTranslation("entries");
	return (
		<header className="ent-hist__head">
			<span className="ent-hist__flag">{t("history.adminOnly")}</span>
			<b className="ent-hist__tier">{t("analysis.title")}</b>
			<span className="ent-hist__sub">{t("analysis.subtitle")}</span>
			{status}
		</header>
	);
}

type T = ReturnType<typeof useTranslation<"entries">>["t"];

function failureText(err: unknown, hasLast: boolean, t: T): string {
	if (err instanceof ApiError && err.code === "RATE_LIMITED") return hasLast ? t("analysis.limitWithLast") : t("analysis.limit");
	if (err instanceof ApiError && err.code === "SERVICE_UNAVAILABLE") return hasLast ? t("analysis.downWithLast") : t("analysis.down");
	return errorMessage(err);
}

/** One finding: its kind chip and its sentence, from the kind's one template. */
function FindingRow({ finding: f, noc }: { finding: EntryAnalysisFinding; noc: string }) {
	const { t } = useTranslation("entries");
	const { formatNumber: n } = useUiStrings();
	let text: string;
	switch (f.kind) {
		case "STRENGTH":
			text = t("analysis.finding.STRENGTH", { count: f.games, noc, sport: f.sport.name, gold: n(f.gold), silver: n(f.silver), bronze: n(f.bronze), games: n(f.games) });
			break;
		case "ABOVE_HISTORY":
		case "BELOW_HISTORY":
			text = t(`analysis.finding.${f.kind}_${f.measure}`, { count: f.expected, noc, sport: f.sport.name, expected: n(f.expected), history: n(f.history), year: f.year });
			break;
		case "NOT_IN_EOI":
		case "NO_EBN_ENTRIES":
			text = t(`analysis.finding.${f.kind}`, { count: f.games, noc, sport: f.sport.name, gold: n(f.gold), games: n(f.games) });
			break;
		case "NO_PAST_MEDAL":
			text = t("analysis.finding.NO_PAST_MEDAL", { count: f.games, noc, sport: f.sport.name, games: n(f.games) });
			break;
		case "OVERALL":
			text = t("analysis.finding.OVERALL", { count: f.sports, noc, sports: n(f.sports), expected: n(f.expected), history: n(f.history), year: f.year });
			break;
	}
	return (
		<li className="ent-ai__item" data-kind={f.kind}>
			<span className="ent-ai__kind">{t(`analysis.kind.${f.kind}`)}</span>
			<span className="ent-ai__text">
				{text}
				{f.kind === "STRENGTH" && <span className="ent-ai__rank">{f.rank === 1 ? t("analysis.rankFirst") : t("analysis.rank", { rank: n(f.rank) })}</span>}
			</span>
		</li>
	);
}
